<script lang="ts">
export default {
  name: 'GenericRangeFilter',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import FilterButton from '@/components/v2/FilterButton.vue';
import FilterRangeMenu from '@/components/v2/FilterRangeMenu.vue';

const props = withDefaults(
  defineProps<{
    modelValue: number[];
    title: string;
    minTitle?: string;
    maxTitle?: string;
    /** When no value is applied, menu inputs are pre-filled with this range (e.g. [0, max] from data). */
    suggestedRange?: number[];
    /** Optional formatter for chip and menu hints (e.g. compact money). */
    valueFormatterFn?: (value: number | undefined | null) => string | number;
    dataTest?: string;
    dataTestPrefix?: string;
  }>(),
  {
    minTitle: 'Minimum',
    maxTitle: 'Maximum',
    dataTestPrefix: 'range-filter',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: number[]];
}>();

const chipTitle = computed(() => {
  const val = props.modelValue;
  if (!val?.length || val.length < 2) return props.title;
  const format = (n: number) => (props.valueFormatterFn ? String(props.valueFormatterFn(n)) : String(n));
  return `${format(val[0])} – ${format(val[1])}`;
});

const isActive = computed(() => (props.modelValue?.length ?? 0) > 0);

function onSave(payload: unknown) {
  if (Array.isArray(payload)) emit('update:modelValue', payload);
}
</script>

<template>
  <FilterButton
    :title="chipTitle"
    :active="isActive"
    start
    :data-test-activator="dataTest"
    @save="onSave">
    <template #menu="{ save }">
      <FilterRangeMenu
        :model-value="modelValue"
        :title="title"
        :min-title="minTitle"
        :max-title="maxTitle"
        :suggested-range="suggestedRange"
        :value-formatter-fn="valueFormatterFn"
        :data-test-prefix="dataTestPrefix"
        @apply="(val) => save(val)" />
    </template>
  </FilterButton>
</template>
